import type {
  CommandErrorCode,
  ExternalSyncState,
} from "../../lib/command-contract";

/**
 * Contrato local do canal de e-mail (Plano 02, Lote 4).
 *
 * Nada aqui abre socket, credencial ou rede. O domínio é neutro a canal para
 * receber WhatsApp/Instagram no futuro sem abstração especulativa: apenas o
 * discriminador `channel` é compartilhado. A v1 conectada usa somente `email`;
 * os demais canais existem no domínio e na demonstração local do Frontend V2,
 * sem transporte, credencial ou envio real.
 */
export const COMMUNICATION_CHANNELS = ["email", "whatsapp", "instagram", "messenger"] as const;
export type CommunicationChannel = (typeof COMMUNICATION_CHANNELS)[number];

export const EMAIL_CHANNEL = "email" as const;
export const EMAIL_PROVIDER = "cpanel.email" as const;
export const MAILBOX_TIME_ZONE = "America/Sao_Paulo" as const;

export const MAILBOX_FOLDERS = ["inbox", "sent"] as const;
export type MailboxFolder = (typeof MAILBOX_FOLDERS)[number];

/**
 * Headers preservados na importação. Qualquer outro header é descartado: log,
 * auditoria e persistência não recebem cabeçalhos arbitrários.
 */
export const ALLOWED_MESSAGE_HEADERS = [
  "message-id",
  "in-reply-to",
  "references",
  "date",
  "subject",
  "from",
  "to",
  "cc",
  "auto-submitted",
  "x-autoreply",
  "list-unsubscribe",
  "content-type",
] as const;
export type AllowedMessageHeader = (typeof ALLOWED_MESSAGE_HEADERS)[number];

export type MailboxSyncState = ExternalSyncState;

/**
 * Caixa de um canal: a conta por onde as conversas daquele canal acontecem.
 *
 * O registro é um só para todos os canais — a tabela nasceu no lote de e-mail e
 * guarda o nome `email_mailboxes`, mas o discriminador sempre foi o canal. O que
 * é exclusivo do e-mail (conexão IMAP/SMTP e caminhos de pasta) fica opcional
 * aqui e obrigatório em `EmailMailbox`.
 */
export interface ChannelBox {
  id: string;
  ownerId: string;
  connectionId?: string;
  channel: CommunicationChannel;
  provider: string;
  address: string;
  normalizedAddress: string;
  displayName?: string;
  inboxFolderPath?: string;
  sentFolderPath?: string;
  timeZone: typeof MAILBOX_TIME_ZONE;
  active: boolean;
  syncState: MailboxSyncState;
  /** Referência opaca ao secret guardado fora do frontend. Nunca o segredo. */
  secretRef?: string;
  importWindowDays: number;
  warmupStartedAt?: string;
  lastSyncedAt?: string;
  lastErrorCode?: CommandErrorCode;
  createdAt: string;
  updatedAt: string;
}

/** A caixa de e-mail é a única que sincroniza pastas e exige conexão. */
export interface EmailMailbox extends ChannelBox {
  channel: typeof EMAIL_CHANNEL;
  connectionId: string;
  provider: typeof EMAIL_PROVIDER;
  inboxFolderPath: string;
  sentFolderPath: string;
}

export function isEmailMailbox(box: ChannelBox): box is EmailMailbox {
  return (
    box.channel === EMAIL_CHANNEL &&
    typeof box.connectionId === "string" &&
    typeof box.inboxFolderPath === "string" &&
    typeof box.sentFolderPath === "string"
  );
}

export interface MailboxSyncCursor {
  id: string;
  ownerId: string;
  mailboxId: string;
  folder: MailboxFolder;
  folderPath: string;
  /** UIDVALIDITY do servidor; mudança exige reconciliação, não reimportação cega. */
  uidValidity: number;
  /** Marca d'água durável: só avança quando a rodada termina por inteiro. */
  uidNext: number;
  modSeq?: number;
  pageToken?: string;
  /** UID inicial da rodada interrompida, para o `pageToken` continuar válido. */
  resumeSinceUid?: number;
  importedThrough?: string;
  syncState: MailboxSyncState;
  lastErrorCode?: CommandErrorCode;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const THREAD_LINK_STATES = ["linked", "unlinked", "not_commercial", "blocked"] as const;
export type ThreadLinkState = (typeof THREAD_LINK_STATES)[number];

export const THREAD_LINK_REASONS = [
  "references",
  "known_thread",
  "exact_endpoint",
  "manual",
  "no_match",
  "ambiguous_lead",
  "ambiguous_company",
  "conflicting_contact",
  "outbound_origin",
] as const;
export type ThreadLinkReason = (typeof THREAD_LINK_REASONS)[number];

export const THREAD_STATES = ["open", "snoozed", "resolved"] as const;
export type ThreadState = (typeof THREAD_STATES)[number];

export interface CommunicationThread {
  id: string;
  ownerId: string;
  channel: CommunicationChannel;
  mailboxId: string;
  companyId?: string;
  contactId?: string;
  leadId?: string;
  funnelId?: string;
  subject: string;
  normalizedSubject: string;
  /** Endpoint externo fixado da conversa, já normalizado. */
  counterpartAddress: string;
  linkState: ThreadLinkState;
  linkReason: ThreadLinkReason;
  state: ThreadState;
  snoozedUntil?: string;
  needsReply: boolean;
  unread: boolean;
  /** Conversa descartada: sai das listas, preserva histórico e rascunho. */
  trashedAt?: string;
  /** Conversa marcada como favorita na Inbox. */
  starredAt?: string;
  lastMessageAt: string;
  lastInboundAt?: string;
  lastOutboundAt?: string;
  /** Versão otimista usada por `expectedVersion` nos comandos. */
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * `received` é o único estado de entrada e existe porque a Inbox persiste
 * mensagens recebidas; os demais estados pertencem a mensagens de saída.
 */
export const INBOUND_MESSAGE_STATES = ["received"] as const;
export const OUTBOUND_MESSAGE_STATES = [
  "draft",
  "queued",
  "submitting",
  "sent",
  "delivered",
  "deferred",
  "bounced",
  "rejected",
  "complained",
  "suppressed",
  "cancelled",
  "append_pending",
] as const;
export const MESSAGE_STATES = [...INBOUND_MESSAGE_STATES, ...OUTBOUND_MESSAGE_STATES] as const;
export type MessageState = (typeof MESSAGE_STATES)[number];
export type OutboundMessageState = (typeof OUTBOUND_MESSAGE_STATES)[number];

export type MessageDirection = "inbound" | "outbound";

/** Estados terminais: não voltam para a fila sem um novo comando explícito. */
export const TERMINAL_MESSAGE_STATES: readonly MessageState[] = [
  "received",
  "delivered",
  "bounced",
  "rejected",
  "complained",
  "suppressed",
  "cancelled",
];

export interface CommunicationMessage {
  id: string;
  ownerId: string;
  threadId: string;
  mailboxId: string;
  channel: CommunicationChannel;
  direction: MessageDirection;
  folder: MailboxFolder;
  uid?: number;
  uidValidity?: number;
  /** `Message-ID` RFC 5322 normalizado, sem os delimitadores angulares. */
  externalMessageId: string;
  inReplyTo?: string;
  references: string[];
  fromAddress: string;
  fromName?: string;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string;
  bodyText: string;
  bodyPreview: string;
  headers: Partial<Record<AllowedMessageHeader, string>>;
  state: MessageState;
  occurredAt: string;
  importedAt: string;
  /** Hash estável do conteúdo, usado como evidência complementar de duplicata. */
  contentHash: string;
  hasAttachments: boolean;
  /**
   * Marcas da mensagem dentro da conversa (§27.3, porte do Zênite).
   *
   * Favorito e fixado são estados do registro, não cópias: a mensagem continua
   * uma só, e a faixa de fixadas e o menu de favoritas são recortes dela.
   * `deletedAt` é exclusão de conteúdo com registro preservado — quem apagou
   * some da bolha, mas a conversa não ganha um buraco silencioso.
   */
  starredAt?: string;
  pinnedAt?: string;
  deletedAt?: string;
  /** Mensagem citada por esta resposta, sempre da mesma conversa. */
  replyToId?: string;
  templateVersionId?: string;
  commandId?: string;
  correlationId?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export const DELIVERY_STATES = OUTBOUND_MESSAGE_STATES;
export type DeliveryState = OutboundMessageState;

export const APPEND_STATES = ["not_required", "pending", "appended", "failed"] as const;
export type AppendState = (typeof APPEND_STATES)[number];

export type BounceType = "transient" | "permanent";

/**
 * `simulated` marca tentativas produzidas pelo fake local. Só `live` — que
 * exige worker, SMTP real e revisão humana — pode gerar interação real.
 */
export const DELIVERY_MODES = ["simulated", "live"] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export interface MessageDelivery {
  id: string;
  ownerId: string;
  messageId: string;
  mailboxId: string;
  attempt: number;
  state: DeliveryState;
  deliveryMode: DeliveryMode;
  idempotencyKey: string;
  outboxJobId?: string;
  providerRequestId?: string;
  smtpCode?: string;
  /** Resposta do servidor já sanitizada: sem body, headers livres ou credencial. */
  sanitizedResponse?: string;
  bounceType?: BounceType;
  appendState: AppendState;
  suppressionId?: string;
  errorCode?: CommandErrorCode;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
}

export const ATTACHMENT_SCAN_STATES = ["pending", "clean", "blocked", "unavailable"] as const;
export type AttachmentScanState = (typeof ATTACHMENT_SCAN_STATES)[number];

/** Anexos entram somente como metadados; bytes exigem download autorizado futuro. */
export interface MessageAttachmentMeta {
  id: string;
  ownerId: string;
  messageId: string;
  filename: string;
  declaredMimeType: string;
  detectedMimeType?: string;
  size: number;
  contentHash: string;
  scanState: AttachmentScanState;
  storagePath?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageTemplate {
  id: string;
  ownerId: string;
  channel: CommunicationChannel;
  name: string;
  description?: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Versão publicada é imutável: `latest` nunca é referência reproduzível. */
export interface MessageTemplateVersion {
  id: string;
  ownerId: string;
  templateId: string;
  version: number;
  status: "draft" | "published";
  subject: string;
  bodyText: string;
  variables: string[];
  optOutIncluded: boolean;
  publishedAt?: string;
  createdAt: string;
}

export const SUPPRESSION_REASONS = [
  "unsubscribe",
  "complaint",
  "hard_bounce",
  "manual_block",
  "not_interested",
] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

export const GLOBAL_SUPPRESSION_REASONS: readonly SuppressionReason[] = [
  "unsubscribe",
  "complaint",
  "hard_bounce",
  "manual_block",
];

export interface ChannelSuppression {
  id: string;
  ownerId: string;
  channel: CommunicationChannel;
  normalizedAddress: string;
  scope: "global" | "funnel";
  funnelId?: string;
  reason: SuppressionReason;
  evidenceMessageId?: string;
  createdAt: string;
}

export const threadLinkStateLabels: Record<ThreadLinkState, string> = {
  linked: "Vinculada",
  unlinked: "Não vinculada",
  not_commercial: "Não comercial",
  blocked: "Bloqueada",
};

export const threadLinkReasonLabels: Record<ThreadLinkReason, string> = {
  references: "Cabeçalho References/In-Reply-To",
  known_thread: "Conversa já vinculada ao mesmo endpoint",
  exact_endpoint: "Endpoint exato da empresa ou do contato",
  manual: "Decisão humana",
  no_match: "Nenhuma correspondência encontrada",
  ambiguous_lead: "Mais de um Lead possível",
  ambiguous_company: "Mais de uma empresa possível",
  conflicting_contact: "Contato conflitante na mesma empresa",
  outbound_origin: "Conversa iniciada pelo CosmosLeads",
};

export const messageStateLabels: Record<MessageState, string> = {
  received: "Recebida",
  draft: "Rascunho",
  queued: "Na fila",
  submitting: "Enviando",
  sent: "Aceita pelo servidor",
  delivered: "Entregue",
  deferred: "Adiada pelo servidor",
  bounced: "Retornada",
  rejected: "Rejeitada",
  complained: "Marcada como spam",
  suppressed: "Bloqueada por supressão",
  cancelled: "Cancelada",
  append_pending: "Aceita, sem cópia em Enviados",
};

export const suppressionReasonLabels: Record<SuppressionReason, string> = {
  unsubscribe: "Descadastro solicitado",
  complaint: "Denúncia de spam",
  hard_bounce: "Retorno permanente",
  manual_block: "Bloqueio manual",
  not_interested: "Sem interesse neste funil",
};

export const mailboxSyncStateLabels: Record<MailboxSyncState, string> = {
  disconnected: "Desconectada",
  pending: "Pendente",
  synced: "Sincronizada",
  error: "Com erro",
  revoked: "Acesso revogado",
};
